import { forbiddenQueryPatterns } from 'static/constants'
import { fromJS, Set as ImmutableSet, List as ImmutableList, Map as ImmutableMap } from 'immutable'
import moment from 'moment-timezone'
import camelcase from 'camelcase'
import { socialMediaUrlPatterns, validateSocialMediaUrl } from 'utils/regex'

const RECENTLY_RESOLVED_ERROR_REPORT_DAYS = 7

// Social media validation helpers used by contact forms.
const allowedChannels = Object.keys(socialMediaUrlPatterns)
const filterChannelUrls = data => data.filter((_value, key) => allowedChannels.includes(key))
export const isValidSocialMediaUrl = (key, url) => (url ? validateSocialMediaUrl(key, url) : true)

export const checkAllUrls = d => filterChannelUrls(d).map((value, key) => ({
  key,
  valid: isValidSocialMediaUrl(key, value)
}))
export const getInvalidChannelUrls = d => checkAllUrls(d).filter(e => !e.valid)

// Search query helpers used by contact and journalist search UIs.
export const isValidSearchQuery = query => {
  if (query && query.length < 3) {
    return false
  }

  if (query.trim() === 'NOT') {
    return false
  }

  return !forbiddenQueryPatterns.some(f => (query.match(f.pattern) || []).length > f.allowedCount)
}

export const validateQueryOnChange = (query, onSuccess) => {
  if (!query || isValidSearchQuery(query)) {
    onSuccess()
  }
}

export const validateQueryOnSubmit = (query, onSuccess, onError) => {
  if (!isValidSearchQuery(query)) {
    onError()
  } else {
    onSuccess()
  }
}

// Location/filter helpers used by person filter forms.
export const filterStateIdsByCountryIds = (selectedStateIds, selectedCountryIds, states) => {
  if (!selectedCountryIds.size) {
    return fromJS([])
  }

  return selectedStateIds.filter(stateId => {
    const state = states.find(s => s.get('id') === stateId)

    return selectedCountryIds.includes(state.get('countryId'))
  })
}

// Contact display helpers used by contact list items.
export const renderInitials = data => {
  if (data.get('firstName')) {
    return `${data.get('firstName')[0]}${data.get('lastName')[0]}`
  }

  return data.get('lastName')[0]
}

const isUnresolvedErrorReport = errorReport => errorReport.get('resolvedAt') == null

const isRecentlyResolvedErrorReport = errorReport => {
  const resolvedAt = errorReport.get('resolvedAt')

  if (resolvedAt == null) {
    return false
  }

  const resolvedAtMoment = moment(resolvedAt)

  return resolvedAtMoment.isValid()
    && moment().diff(resolvedAtMoment, 'days', true) < RECENTLY_RESOLVED_ERROR_REPORT_DAYS
}

const hasMatchingErrorReport = (errorReports, fieldName, predicate) => !!errorReports
  && !!errorReports.find(errorReport => errorReport.get('fieldName') === fieldName && predicate(errorReport))

export const getContactErrorReportStatus = (contact, fieldName) => {
  const errorReports = contact.get('errorReports')
  const reported = hasMatchingErrorReport(errorReports, fieldName, isUnresolvedErrorReport)

  return {
    reported,
    resolved: !reported && hasMatchingErrorReport(errorReports, fieldName, isRecentlyResolvedErrorReport)
  }
}

// Reducer helpers used by reducers/contact_management/data.
export const normalizeContact = contactData => {
  let contact = fromJS(contactData)

  if (contact.has('languageIds')) {
    contact = contact.set('languageIds', ImmutableSet(contact.get('languageIds')))
  }

  if (contact.has('iptcMediaTopicIds')) {
    contact = contact.set('iptcMediaTopicIds', ImmutableSet(contact.get('iptcMediaTopicIds')))
  }

  if (contact.has('ghost') && contact.get('ghost')?.has('languageIds')) {
    contact = contact.setIn(['ghost', 'languageIds'], ImmutableSet(contact.getIn(['ghost', 'languageIds'])))
  }

  if (contact.has('ghost') && contact.get('ghost')?.has('iptcMediaTopicIds')) {
    contact = contact.setIn(['ghost', 'iptcMediaTopicIds'], ImmutableSet(contact.getIn(['ghost', 'iptcMediaTopicIds'])))
  }

  return contact
}

export const normalizeContacts = contacts => fromJS(contacts).map(normalizeContact)

const mergeDistributionLists = (primaryLists, fallbackLists) => {
  const primary = primaryLists || fromJS([])
  const fallback = fallbackLists || fromJS([])

  if (primary.size > 0) {
    return primary
  }

  return fallback
}

export const mergeContactData = (primaryContact, fallbackContact, fallbackDistributionList) => {
  if (!primaryContact && !fallbackContact) {
    return null
  }

  let mergedContact = fromJS(fallbackContact || {}).merge(fromJS(primaryContact || {}))
  const preservedDistributionLists = mergeDistributionLists(
    mergedContact.get('distributionLists'),
    fromJS(fallbackContact || {}).get('distributionLists')
  )
  const currentDistributionLists = mergedContact.get('distributionLists') || fromJS([])

  if (preservedDistributionLists.size > 0 && currentDistributionLists !== preservedDistributionLists) {
    mergedContact = mergedContact.set('distributionLists', preservedDistributionLists)
  }

  if ((!currentDistributionLists || currentDistributionLists.isEmpty()) && fallbackDistributionList?.get('id')) {
    return mergedContact.set('distributionLists', fromJS([{
      id: fallbackDistributionList.get('id'),
      name: fallbackDistributionList.get('name')
    }]))
  }

  return mergedContact
}

const getOptimisticDistributionLists = (state, distributionListIds) => {
  const currentContactLists = state.getIn(['contactDetailsData', 'contact', 'distributionLists']) || fromJS([])
  const loadedDistributionLists = state.get('distributionLists')

  return fromJS(distributionListIds).map(distributionListId => {
    const loadedDistributionList = loadedDistributionLists.find(list => list.get('id') === distributionListId)

    if (loadedDistributionList) {
      return fromJS({
        id: loadedDistributionList.get('id'),
        name: loadedDistributionList.get('name')
      })
    }

    return currentContactLists.find(list => list.get('id') === distributionListId) || fromJS({ id: distributionListId })
  })
}

export const applyFakeContactAttributes = (state, payload) => {
  const currentContact = state.getIn(['contactDetailsData', 'contact'])

  if (!currentContact || currentContact.isEmpty() || currentContact.get('id') !== payload.id) {
    return state
  }

  const optimisticAttributes = Object.entries(payload.attributes).reduce((acc, [key, value]) => {
    if (key === 'distribution_list_ids') {
      return acc.set('distributionLists', getOptimisticDistributionLists(state, value))
    }

    if (key === 'country_id') {
      return acc.set('stateId', null).set('countryId', value)
    }

    return acc.set(camelcase(key), fromJS(value))
  }, fromJS({}))

  return state.setIn(['contactDetailsData', 'contact'], normalizeContact(currentContact.merge(optimisticAttributes)))
}

export const revertFakeContactUpdate = (state, payload) => {
  const currentContact = state.getIn(['contactDetailsData', 'contact'])

  if (!currentContact || currentContact.isEmpty() || currentContact.get('id') !== payload.id) {
    return state
  }

  const originalContact = state.get('contacts').find(contact => contact.get('id') === payload.id)

  if (!originalContact) {
    return state
  }

  return state.setIn(['contactDetailsData', 'contact'], normalizeContact(originalContact))
}

const emptyDistributionList = ImmutableMap({ contacts: ImmutableList() })

export const ensureDistributionListShape = list => {
  if (!ImmutableMap.isMap(list)) {
    return emptyDistributionList
  }

  const contacts = list.get('contacts')

  if (ImmutableList.isList(contacts)) {
    return list
  }

  return list.set('contacts', ImmutableList())
}

export const getFullName = contact => {
  if (contact.get('name')) {
    return contact.get('name')
  }

  return `${contact.get('firstName') || ''} ${contact.get('lastName') || ''}`.trim()
}
